"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { Menu, X, Phone } from "lucide-react";
import { type Locale, localizePath, parsePathname } from "@/lib/i18n/config";
import { getDictionary } from "@/lib/i18n/dictionaries";

type Props = { locale: Locale };

export function MobileDrawer({ locale }: Props) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const dict = getDictionary(locale);
  const nav = dict.nav;
  const common = dict.common;
  const link = (p: string) => localizePath(p, locale);
  const { path: currentPath } = parsePathname(pathname || "/");

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      document.documentElement.classList.add("nav-open");
      document.body.style.overflow = "hidden";
    } else {
      document.documentElement.classList.remove("nav-open");
      document.body.style.overflow = "";
    }
    return () => {
      document.documentElement.classList.remove("nav-open");
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Demo-rækkefølge: Forside → Personbil → Motorcykel → Trailer → Generhvervelse → Priser → Butik → Holdstart → Om os → Kontakt
  const items = [
    { href: "/", label: nav.forside },
    { href: "/personbil", label: nav.bil },
    { href: "/motorcykel", label: nav.motorcykel },
    { href: "/trailer", label: nav.trailer },
    { href: "/generhvervelse", label: nav.generhvervelse },
    { href: "/priser", label: nav.priser },
    { href: "/butik", label: nav.butik },
    { href: "/holdstart", label: nav.holdstart },
    { href: "/om-os", label: nav.omOs },
    { href: "/kontakt", label: nav.kontakt },
  ];

  // Drawer-overlay rendres via React Portal direkte på <body> for at undgå
  // ancestor stacking-context fra Header'ens backdrop-filter.
  const drawerNode = (
    <aside
      id="navDrawer"
      data-track-area="menu"
      aria-hidden={!open}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,23,37,0.92)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        color: "#fff",
        zIndex: 9999,
        opacity: open ? 1 : 0,
        transition: "opacity 250ms ease",
        overflowY: "auto",
        padding: "28px 28px 36px",
        // Når lukket: helt usynlig + ude af layout-flow så den ikke skaber horizontal scroll
        visibility: open ? "visible" : "hidden",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      <div className="flex items-center justify-between mb-6">
        <Image
          src="/images/broendby-koereskole-logo-horisontalt-hvid.webp"
          alt="Brøndby Køreskole"
          width={200}
          height={60}
          className="h-[60px] w-auto"
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label={common.closeMenu}
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: "rgba(255,255,255,0.1)", color: "#fff" }}
        >
          <X size={20} />
        </button>
      </div>

      <nav>
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {items.map((item) => {
            const isActive = currentPath === item.href || (item.href === "/" && currentPath === "/");
            return (
              <li key={item.href}>
                <Link
                  href={link(item.href)}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-between"
                  style={{
                    padding: "18px 0",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: isActive ? "#76c169" : "#fff",
                  }}
                >
                  <span>{item.label}</span>
                  <span style={{ color: "#76c169" }}>→</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="mt-10">
        <Link
          href={link("/holdstart")}
          onClick={() => setOpen(false)}
          className="flex items-center justify-center font-semibold rounded-[12px] py-4 text-[16px]"
          style={{ background: "#76c169", color: "#fff" }}
        >
          {nav.seHold}
        </Link>
        <a
          href="tel:+4552404020"
          className="flex items-center gap-2 mt-4 text-[14px]"
          style={{ color: "rgba(255,255,255,0.75)" }}
        >
          <Phone size={14} strokeWidth={2.2} />
          52 40 40 20 · {common.openingHoursWeekdays}
        </a>
      </div>
    </aside>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={common.openMenu}
        aria-expanded={open}
        aria-controls="navDrawer"
        className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[var(--color-line)] bg-white text-[var(--color-navy-700)]"
        style={{ opacity: open ? 0 : 1, transition: "opacity 200ms" }}
      >
        <Menu size={20} />
      </button>
      {/* Render drawer via portal direkte på document.body — undgår
          ancestor stacking-context (backdrop-filter på Header). */}
      {mounted && typeof document !== "undefined"
        ? createPortal(drawerNode, document.body)
        : null}
    </>
  );
}
